/**
 * Server-side run validation (also usable client-side for the offline demo
 * backend). Replays the run, recomputes scores, checks timing plausibility.
 * Spec: docs/GAME_DESIGN.md § Anti-cheat. Constants: config.ts ANTICHEAT.
 *
 * Severity: `reject` anomalies make `ok` false (the run is not stored as
 * valid); `flag` anomalies keep the run but hide it from leaderboards until
 * reviewed. The recomputed summary is authoritative — claimed values are only
 * compared, never trusted.
 */
import type { Widen } from '../adaptive/types.ts';
import { ANTICHEAT, RULES_VERSION } from '../config.ts';
import { toPlain } from '../algebra/markup.ts';
import { ReplayError, replayRunTrace, type ReplayTrace } from '../game/replay.ts';
import { summarize } from '../game/summary.ts';
import { cv, median } from '../math/stats.ts';
import { answerTime, ratingAnchor, relevanceWeight } from '../scoring/index.ts';
import type { Anomaly, AnswerRecord, GameConfig, GameEvent, Problem, RunSummary, ValidationReport } from '../types.ts';

export interface ValidationInput {
  rulesVersion: number;
  seed: number;
  startRating: number;
  referenceRating: number;
  events: readonly GameEvent[];
  claimed: { raw: number; rated: number };
  /** Server wall-clock ms between session issue and submit. */
  wallClockMs: number;
  /**
   * Engine config the session was issued with — `paceConfig(relaxed)` from the
   * session record, never from the client. A run played at another pace fails
   * the replay (answer-after-lock) or the score check.
   */
  config?: Partial<GameConfig>;
}

/** ANTICHEAT with plain `number` fields, so tests can pass tuned thresholds. */
export type AntiCheatConstants = Widen<typeof ANTICHEAT>;

export function validateRun(input: ValidationInput, k: AntiCheatConstants = ANTICHEAT): ValidationReport {
  if (input.rulesVersion !== RULES_VERSION) {
    // A run made under other rules can't be replayed meaningfully — reject without replaying.
    return report(
      [reject('rules-version', `run uses rules v${input.rulesVersion}, server runs v${RULES_VERSION}`)],
      null,
    );
  }

  // Cheap bound before the (linear) replay: an event stamped later than the
  // session could possibly have run already proves the duration check fails.
  // Rejecting here keeps absurd logs from costing a full replay.
  const lastAt = latestEventTime(input.events);
  if (lastAt > input.wallClockMs + k.WALL_CLOCK_SLACK_MS) {
    return report([wallClockAnomaly(lastAt, input.wallClockMs, k)], null);
  }

  let trace: ReplayTrace;
  try {
    trace = replayRunTrace({
      seed: input.seed,
      startRating: input.startRating,
      referenceRating: input.referenceRating,
      events: input.events,
      ...(input.config ? { config: input.config } : {}),
    });
  } catch (err) {
    if (err instanceof ReplayError) return report([reject(err.code, err.message)], null);
    throw err; // an engine failure is a server bug, not a verdict on the player
  }

  const summary = summarize(trace.state);
  const history = trace.state.history;
  // The same window RatedScore uses: answers far below max(reference, run peak) don't count.
  const counted = countedAnswers(history, ratingAnchor(input.referenceRating, summary.peakRating));
  const anomalies = [
    ...scoreChecks(input, summary),
    ...durationChecks(input, summary, k),
    ...timingChecks(history, trace.answered, k),
    ...nearPerfectChecks(counted, k),
    ...rateChecks(history, summary.durationMs, counted, k),
  ];
  return report(anomalies, summary);
}

function report(anomalies: Anomaly[], summary: RunSummary | null): ValidationReport {
  return {
    ok: !anomalies.some((a) => a.severity === 'reject'),
    flagged: anomalies.some((a) => a.severity === 'flag'),
    anomalies,
    summary,
  };
}

const reject = (code: Anomaly['code'], detail: string): Anomaly => ({ code, severity: 'reject', detail });
const flag = (code: Anomaly['code'], detail: string): Anomaly => ({ code, severity: 'flag', detail });

/** Claimed scores must equal the replayed ones exactly (both are integers). */
function scoreChecks(input: ValidationInput, summary: RunSummary): Anomaly[] {
  const { raw, rated } = input.claimed;
  if (raw === summary.raw && rated === summary.rated) return [];
  return [reject('score-mismatch', `claimed raw ${raw} / rated ${rated}, replay gives raw ${summary.raw} / rated ${summary.rated}`)];
}

/** The engine clock excludes pauses, so it can never exceed the session's wall-clock time (+ slack). */
function durationChecks(input: ValidationInput, summary: RunSummary, k: AntiCheatConstants): Anomaly[] {
  if (summary.durationMs <= input.wallClockMs + k.WALL_CLOCK_SLACK_MS) return [];
  return [wallClockAnomaly(summary.durationMs, input.wallClockMs, k)];
}

function wallClockAnomaly(engineMs: number, wallClockMs: number, k: AntiCheatConstants): Anomaly {
  return reject(
    'engine-time-exceeds-wall',
    `engine time ${Math.round(engineMs)} ms exceeds wall clock ${Math.round(wallClockMs)} ms + ${k.WALL_CLOCK_SLACK_MS} ms`,
  );
}

/** Largest numeric `at` in the log (malformed events are left for the replay to reject). */
function latestEventTime(events: readonly GameEvent[]): number {
  let latest = 0;
  for (const event of events as readonly unknown[]) {
    const at = typeof event === 'object' && event !== null ? (event as { at?: unknown }).at : undefined;
    if (typeof at === 'number' && at > latest) latest = at;
  }
  return latest;
}

/** Minimum plausible time to read a prompt and answer it: base + per visible character. */
export function minHumanResponseMs(problem: Pick<Problem, 'prompt'>, k: AntiCheatConstants = ANTICHEAT): number {
  return k.MIN_HUMAN_MS_BASE + k.MIN_HUMAN_MS_PER_CHAR * toPlain(problem.prompt).length;
}

function timingChecks(
  history: readonly AnswerRecord[],
  answered: ReadonlyMap<string, Problem>,
  k: AntiCheatConstants,
): Anomaly[] {
  const anomalies: Anomaly[] = [];
  const correct = history.filter((r) => r.outcome === 'correct');
  const taus = correct.map((r) => r.responseMs / r.parTimeMs);

  // Too fast to have read the sum. Reject needs both the flag count and the fraction,
  // so one lucky tap in a 3-answer run doesn't reject it.
  const tooFast = correct.filter((r) => {
    const problem = answered.get(r.problemId);
    return problem !== undefined && r.responseMs < minHumanResponseMs(problem, k);
  }).length;
  if (tooFast >= k.TOO_FAST_FLAG_COUNT) {
    const fraction = history.length > 0 ? tooFast / history.length : 0;
    const detail = `${tooFast} of ${history.length} answers correct faster than the reading minimum`;
    anomalies.push(fraction >= k.TOO_FAST_REJECT_FRACTION ? reject('too-fast', detail) : flag('too-fast', detail));
  }

  // Robotic: humans vary; near-identical normalized times over many answers are scripted.
  if (correct.length >= k.ROBOTIC_MIN_ANSWERS) {
    const spread = cv(taus);
    if (spread < k.ROBOTIC_CV) {
      anomalies.push(flag('robotic-timing', `cv(τ) = ${spread.toFixed(3)} over ${correct.length} correct answers`));
    }
  }

  return anomalies;
}

/**
 * The answers that count for RatedScore: relevance weight m > 0 at the run's
 * anchor rating (see scoring/rated.ts). Answers far below the anchor — the
 * climb of a first run, or easy sums farmed on the way up — neither raise nor
 * lower the score, so the checks that protect the boards ignore them too: an
 * honest climber is near-perfect and fast on them, and a cheater gains nothing
 * from them.
 */
export function countedAnswers(history: readonly AnswerRecord[], anchorRating: number): AnswerRecord[] {
  return history.filter((r) => relevanceWeight(r.level, anchorRating) > 0);
}

/**
 * Near-perfect where it counts. The adaptive engine raises the level until an
 * honest player misses about a third of the sums, so NEAR_PERFECT_ACCURACY or
 * more over the counted answers is what an always-right script looks like
 * (honest sim runs stay below 0.85 over theirs). A short near-perfect stretch
 * also happens to honest players who quit while warming up, so below
 * NEAR_PERFECT_SUSTAINED_ANSWERS it is only flagged at a median of half par
 * or faster; from there on, at any pace.
 */
function nearPerfectChecks(counted: readonly AnswerRecord[], k: AntiCheatConstants): Anomaly[] {
  if (counted.length < k.RELEVANT_MIN_ANSWERS) return [];
  const correct = counted.filter((r) => r.outcome === 'correct');
  if (correct.length < k.NEAR_PERFECT_ACCURACY * counted.length) return [];
  const medianTau = median(correct.map((r) => r.responseMs / r.parTimeMs));
  const sustained = counted.length >= k.NEAR_PERFECT_SUSTAINED_ANSWERS;
  if (!sustained && medianTau > k.NEAR_PERFECT_MEDIAN_TAU) return [];
  const limits = sustained
    ? `accuracy ≥ ${k.NEAR_PERFECT_ACCURACY} over ≥ ${k.NEAR_PERFECT_SUSTAINED_ANSWERS}`
    : `accuracy ≥ ${k.NEAR_PERFECT_ACCURACY}, median τ ≤ ${k.NEAR_PERFECT_MEDIAN_TAU}`;
  return [flag('perfect-superhuman', `${correct.length}/${counted.length} counted answers correct, median τ ${medianTau.toFixed(2)} (${limits})`)];
}

/**
 * Correct answers per par-unit of time over `records`: correct ÷ Σ t̂, with t̂
 * = response ÷ par clamped exactly as RatedScore clamps it. Accuracy and speed
 * in one number — 1.0 is all right at par, 2.0 is all right at half par or 80 %
 * right at 0.4 × par. It is what the RatedScore numerator ÷ denominator
 * rewards, without the difficulty weights and the prior.
 */
export function creditedSpeedup(records: readonly AnswerRecord[]): number {
  const time = records.reduce((sum, r) => sum + answerTime(r), 0);
  if (time === 0) return 0;
  return records.filter((r) => r.outcome === 'correct').length / time;
}

/**
 * Sustained speed-up over par: Σ parTimeMs of correct answers ÷ engine time.
 * Engine time includes every answer's think time plus the fixed resolve and
 * banner phases, so even a flawless player answering at a quarter of par
 * stays well under MAX_PAR_SPEEDUP (the sim's fastest honest runs reach about
 * 3). Only judged over RATE_MIN_ANSWERS answers, so a short lucky burst can't
 * trip it. Unlike a score-per-minute limit, it doesn't depend on the scoring
 * scale or on how long the run lasted.
 */
export function parSpeedup(history: readonly AnswerRecord[], durationMs: number): number {
  const parMs = history.reduce((sum, r) => sum + (r.outcome === 'correct' ? r.parTimeMs : 0), 0);
  if (parMs === 0) return 0;
  return durationMs > 0 ? parMs / durationMs : Number.POSITIVE_INFINITY;
}

/**
 * Two measures of an impossible rate, reported as one `impossible-rate` flag:
 * the whole run's par speed-up (Σ par of correct answers ÷ engine time), and
 * the credited speed-up of the answers that count for RatedScore. The second
 * catches a script that stays just under the near-perfect bar by missing a
 * sum now and then while answering the rest far faster than a person.
 */
function rateChecks(
  history: readonly AnswerRecord[],
  durationMs: number,
  counted: readonly AnswerRecord[],
  k: AntiCheatConstants,
): Anomaly[] {
  if (history.length >= k.RATE_MIN_ANSWERS) {
    const speedup = parSpeedup(history, durationMs);
    if (speedup > k.MAX_PAR_SPEEDUP) {
      return [
        flag(
          'impossible-rate',
          `correct answers worth ${speedup.toFixed(2)}× the engine time in par (limit ${k.MAX_PAR_SPEEDUP}×) over ${history.length} answers`,
        ),
      ];
    }
  }
  if (counted.length >= k.RELEVANT_MIN_ANSWERS) {
    const credited = creditedSpeedup(counted);
    if (credited >= k.MAX_CREDITED_SPEEDUP) {
      return [
        flag(
          'impossible-rate',
          `credited speed-up ${credited.toFixed(2)} (correct ÷ Σ t̂, limit ${k.MAX_CREDITED_SPEEDUP}) over ${counted.length} counted answers`,
        ),
      ];
    }
  }
  return [];
}
